"use strict";

(() => {
    /**
     * @see https://medium.com/@diogobv.batista/a-story-about-a-graph-part-1-77fc29977322
     */
    class Savings_Sentence_1 {
        /** @var {HTMLElement} */
        #$widget;

        /** @var {HTMLInputElement} */
        #$targetInput;

        /** @var {HTMLInputElement} */
        #$initialDepositInput;

        /** @var {HTMLInputElement} */
        #$horizonInput;

        /** @var {HTMLInputElement} */
        #$interestInput;

        /** @var {HTMLInputElement} */
        #$interestTaxInput;

        /** @var {HTMLElement} */
        #$resultWrapper;

        /** @var {HTMLButtonElement} */
        #$showProgressButton;


        /**
         * Variable to store last valid inputs and monthly investment
         * to fill into main savings calculator.
         *
         * @var {object|null}
         */
        #lastValidValues;


        constructor($widget) {
            this.#$widget         = $widget;
            this.#lastValidValues = null;
        }

        initialize() {
            this.#$targetInput         = this.#$widget.querySelector(".dan-input-target");
            this.#$initialDepositInput = this.#$widget.querySelector(".dan-input-initial-deposit");
            this.#$horizonInput        = this.#$widget.querySelector(".dan-input-horizon");
            this.#$interestInput       = this.#$widget.querySelector(".dan-input-interest");
            this.#$interestTaxInput    = this.#$widget.querySelector(".dan-input-interest-tax");
            this.#$resultWrapper       = this.#$widget.querySelector(".dan-sentence-result-wrap");
            this.#$showProgressButton  = this.#$widget.querySelector(".dan-show-progress");

            this.#$widget.addEventListener("submit", (this.#onFormSubmit).bind(this));
            this.#$showProgressButton.addEventListener(
                "click",
                (this.#onShowProgressClick).bind(this)
            );
        }

        /**
         * @param {SubmitEvent} event
         */
        #onFormSubmit(event) {
            event.preventDefault();

            // Load inputs.
            const inputs = this.getInputs();

            if (
                inputs.target === null
                || inputs.initialDeposit === null
                || inputs.horizon === null
                || inputs.interest === null
            ) {
                alert("Prosím, vyplňte správně všechna pole.");
                return;
            }

            const monthlyInvestment = this.#calcMonthlyInvestment(
                inputs.target,
                inputs.initialDeposit,
                inputs.horizon,
                inputs.interest,
                inputs.interestTax
            );

            const savingsValue = this.#calcSavingsValue(
                inputs.initialDeposit,
                monthlyInvestment,
                inputs.horizon,
                inputs.interest,
                inputs.interestTax
            );

            this.#setResult(monthlyInvestment, savingsValue);

            this.#lastValidValues = {
                initialDeposit: inputs.initialDeposit,
                monthlyInvestment: monthlyInvestment,
                horizon: inputs.horizon,
                interest: inputs.interest,
                interestTax: inputs.interestTax
            };
        }

        #calcMonthlyInvestment(
            target,
            initialDeposit,
            horizon,
            interest,
            interestTax
        ) {
            // Calc approximate monthly investment by formula.
            let monthlyInvestmentFormula;
            monthlyInvestmentFormula = this.#calcMonthlyInvestmentByFormula(
                target,
                initialDeposit,
                horizon,
                interest,
                interestTax
            );

            monthlyInvestmentFormula = Math.round(monthlyInvestmentFormula);

            // Correct monthly investment by numeric method.
            return this.#correctMonthlyInvestment(
                monthlyInvestmentFormula,
                target,
                initialDeposit,
                horizon,
                interest,
                interestTax
            );
        }

        #calcMonthlyInvestmentByFormula(
            target,
            initialDeposit,
            horizon,
            interest,
            interestTax
        ) {
            const interestCoeff = (interestTax)
                ? (interest / 100 * 0.85)
                : (interest / 100 );

            const monthlyInvestment = ((
                (target - initialDeposit * Math.pow(1 + interestCoeff, horizon))
                / (
                    (Math.pow(1 + interestCoeff, horizon) - 1)
                    / interestCoeff
                )
            ) / 12);

            return (monthlyInvestment > 0)
                ? monthlyInvestment
                : 0;
        }

        #correctMonthlyInvestment(
            monthlyInvestment,
            target,
            initialDeposit,
            horizon,
            interest,
            interestTax,
        ) {
            const initialSavingsValue = this.#calcSavingsValue(
                initialDeposit,
                monthlyInvestment,
                horizon,
                interest,
                interestTax
            );

            let monthlyInvestmentCorrected = monthlyInvestment;

            while (true) {
                const newMonthlyInvestment = (initialSavingsValue > target)
                    ? (monthlyInvestmentCorrected - 1)
                    : (monthlyInvestmentCorrected + 1);

                if (newMonthlyInvestment <= 0) {
                    break;
                }

                const newSavingsValue = this.#calcSavingsValue(
                    initialDeposit,
                    newMonthlyInvestment,
                    horizon,
                    interest,
                    interestTax
                );

                if (
                    (initialSavingsValue > target && newSavingsValue <= target)
                    || (initialSavingsValue < target && newSavingsValue >= target)
                ) {
                    break;
                }

                monthlyInvestmentCorrected = newMonthlyInvestment;
            }

            return monthlyInvestmentCorrected;
        }

        #calcSavingsValue(
            initialDeposit,
            monthlyInvestment,
            horizon,
            interest,
            interestTax
        ) {
            const interestCoeff = (interest / 100 );
            let savingsValue    = initialDeposit;

            for (let i = 1; i <= horizon; i++) {
                savingsValue += (monthlyInvestment * 12);
                savingsValue += (interestTax)
                    ? (savingsValue * interestCoeff * 0.85)
                    : (savingsValue * interestCoeff);
            }

            return savingsValue;
        }

        #setResult(monthlyInvestment, savingsValue) {
            if (this.#$resultWrapper.classList.contains("-active")) {
                this.#slideResultUp(() => {
                    this.#setResultValues(monthlyInvestment, savingsValue);
                    this.#slideResultDown();
                });
            } else {
                this.#setResultValues(monthlyInvestment, savingsValue);
                this.#slideResultDown();
            }
        }

        #setResultValues(monthlyInvestment, savingsValue) {
            const $monthlyInvestment = this.#$resultWrapper.querySelector(".-monthly");
            const $savingsValue      = this.#$resultWrapper.querySelector(".-value");

            if ($monthlyInvestment) {
                $monthlyInvestment.textContent = Dan_Utilities.formatPrice(monthlyInvestment) + " Kč";
            }

            if ($savingsValue) {
                $savingsValue.textContent = Dan_Utilities.formatPrice(savingsValue) + " Kč";
            }
        }

        #slideResultUp(callback = null) {
            this.#$resultWrapper.style.height = this.#$resultWrapper.scrollHeight + "px";

            setTimeout(() => {
                this.#$resultWrapper.style.height = "0";
            }, 25);

            setTimeout(() => {
                this.#$resultWrapper.classList.remove("-active");

                if (typeof callback === "function") {
                    callback();
                }
            }, 175);
        }

        #slideResultDown(callback = null) {
            this.#$resultWrapper.style.height = this.#$resultWrapper.scrollHeight + "px";

            setTimeout(() => {
                this.#$resultWrapper.style.height = "auto";
                this.#$resultWrapper.classList.add("-active");

                if (typeof callback === "function") {
                    callback();
                }
            }, 150);
        }

        #onShowProgressClick() {
            if (!this.#lastValidValues) {
                return;
            }

            const $savingsCalculators = document.querySelectorAll(".dan-savings-calculator");

            for (const $savingsCalculator of $savingsCalculators) {
                $savingsCalculator.calculator.setInputs(
                    this.#lastValidValues.initialDeposit,
                    this.#lastValidValues.monthlyInvestment,
                    this.#lastValidValues.horizon,
                    this.#lastValidValues.interest,
                    this.#lastValidValues.interestTax
                );

                $savingsCalculator.calculator.refresh();
            }

            // Scroll to the first calculator.
            if ($savingsCalculators.length > 0) {
                $savingsCalculators[0].scrollIntoView({
                    behavior: "smooth"
                });
            }
        }

        getInputs() {
            // Target.
            let target;
            target = this.#$targetInput.value || this.#$targetInput.placeholder;
            target = parseInt(target);

            if (isNaN(target) || target < 0) {
                target = null;
            }

            // Initial deposit.
            let initialDeposit;
            initialDeposit = this.#$initialDepositInput.value || this.#$initialDepositInput.placeholder;
            initialDeposit = parseInt(initialDeposit);

            if (isNaN(initialDeposit) || initialDeposit < 0) {
                initialDeposit = null;
            }

            // Horizon.
            let horizon;
            horizon = this.#$horizonInput.value || this.#$horizonInput.placeholder;
            horizon = parseInt(horizon);

            if (isNaN(horizon) || horizon < 1) {
                horizon = null;
            }

            // Interest.
            let interest;
            interest = this.#$interestInput.value || this.#$interestInput.placeholder;
            interest = parseFloat(interest);

            if (!isNaN(interest) && interest >= 0) {
                interest = (Math.round(interest * 100) / 100);
            } else {
                interest = null;
            }

            // Interest tax.
            const interestTax = (this.#$interestTaxInput.value === "yes");

            // Return.
            return {
                target,
                initialDeposit,
                horizon,
                interest,
                interestTax
            };
        }
    }

    // Initialize widgets.
    const $widgets = document.querySelectorAll("form.dan-savings-sentence-1");

    for (const $widget of $widgets) {
        $widget.calculator = new Savings_Sentence_1($widget);
        $widget.calculator.initialize();
    }
})();
